import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, MessageSquare, Activity, BarChart, Bot, Code, AlertCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { activityApiClient, BotActivity } from "@/api/activityApiClient";

interface ActivityFeedProps {
  aiStaffId?: string;
  limit?: number;
  showTitle?: boolean;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ 
  aiStaffId, 
  limit = 10,
  showTitle = true
}) => {
  const [activities, setActivities] = useState<BotActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActivities = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await activityApiClient.fetchActivities(aiStaffId, limit);
        setActivities(data);
      } catch (err) {
        logger.error({
          message: "Exception fetching bot activities",
          error: err instanceof Error ? err : new Error(String(err)),
          context: { aiStaffId }
        });
        setError("An unexpected error occurred");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchActivities();
    
    // Set up subscription to real-time updates
    const subscription = supabase
      .channel('bot-activities-changes')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'bot_activities',
        ...(aiStaffId ? { filter: `ai_staff_id=eq.${aiStaffId}` } : {})
      }, (payload) => {
        setActivities(prevActivities => {
          const newActivity = payload.new as BotActivity;
          // Insert at beginning and maintain limit
          return [newActivity, ...prevActivities].slice(0, limit);
        });
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(subscription);
    };
  }, [aiStaffId, limit]);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'slack_command':
      case 'slack_mention':
      case 'slack_webhook':
        return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case 'prompt_received':
        return <Bot className="h-5 w-5 text-purple-500" />;
      case 'response_generated':
        return <Code className="h-5 w-5 text-green-500" />;
      case 'api_request':
      case 'direct_api_call':
        return <Activity className="h-5 w-5 text-orange-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <BarChart className="h-5 w-5 text-gray-500" />;
    }
  };

  const getFormattedTime = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (error) {
      return 'unknown time';
    }
  };

  return (
    <Card className="shadow-sm">
      {showTitle && (
        <CardHeader className="pb-2">
          <CardTitle className="text-xl flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Activity Feed
          </CardTitle>
        </CardHeader>
      )}
      
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="space-y-4">
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="flex gap-3 items-start">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-4 text-center text-muted-foreground">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-red-500" />
            <p>{error}</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No activities recorded yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activities.map((activity) => (
              <div key={activity.id} className="flex gap-3 items-start animate-in fade-in slide-in-from-bottom-4 duration-300">
                <div className="mt-0.5 rounded-full p-1.5 bg-muted flex items-center justify-center">
                  {getActivityIcon(activity.activity_type)}
                </div>
                <div>
                  <p className="text-sm font-medium">{activity.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground">
                      {getFormattedTime(activity.created_at)}
                    </span>
                    <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
                    <span className="text-xs font-medium text-muted-foreground capitalize">
                      {activity.activity_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
